// Derives an RxDB JSON Schema from a Zod schema.
//
// Pairs with `wireFromDrizzle`: the wire Zod schema describes what flows
// over the network, this helper post-processes it into the JSON-Schema
// shape RxDB stores in IndexedDB. The two concerns the helper handles
// that aren't expressible in plain Zod:
//   1. `_deleted` is wire-only. RxDB manages tombstones internally based
//      on the `deletedField` config on the replication; the collection
//      schema must not declare the field.
//   2. RxDB needs `maxLength` on indexed string columns and on the
//      primary key (it encodes them as fixed-width keys); date-time
//      strings get a sensible 30-char default. Length isn't expressible
//      via Zod's `.string()` without a regex.
//
// Numeric bounds and indexes don't come from the table definition
// either — they're RxDB-side performance knobs — so they're explicit
// parameters.

import { type RxJsonSchema, type TopLevelProperty } from 'rxdb';
import { type z } from 'zod/v4';

type NumericProp = {
  maximum?: number;
  minimum?: number;
  multipleOf?: number;
  type: 'integer' | 'number';
};

const DATE_TIME_MAX_LENGTH = 30;
export type RxdbFromZodOptions<T> = {
  indexes?: ReadonlyArray<(keyof T & string) | ReadonlyArray<keyof T & string>>;
  numericRanges?: Partial<
    Record<
      keyof T & string,
      { max?: number; min?: number; multipleOf?: number }
    >
  >;
  primaryKey: keyof T & string;
  stringMaxLength?: Partial<Record<keyof T & string, number>>;
  version: number;
};

const stripProperty = (raw: unknown): TopLevelProperty => {
  if (!raw || typeof raw !== 'object') {
    return raw as TopLevelProperty;
  }

  const cleaned: Record<string, unknown> = { ...(raw as object) };

  // Unwind Zod's `anyOf: [{ type: 'X', ... }, { type: 'null' }]` into
  // `type: ['X', 'null']` — RxDB's JSON Schema parser doesn't support
  // `anyOf` at the property level, but the JSON Schema union-type form
  // is widely accepted.
  const anyOf = cleaned.anyOf as Array<Record<string, unknown>> | undefined;
  if (anyOf && anyOf.length === 2) {
    const nullBranch = anyOf.find((branch) => branch.type === 'null') as
      | Record<string, unknown>
      | undefined;
    const valueBranch = anyOf.find((branch) => branch.type !== 'null') as
      | Record<string, unknown>
      | undefined;
    if (nullBranch && valueBranch) {
      delete cleaned.anyOf;
      cleaned.type =
        valueBranch.type === undefined
          ? ['object', 'null']
          : [valueBranch.type, 'null'];
      if (valueBranch.type === undefined) {
        cleaned.additionalProperties = true;
      }

      // Carry over any constraints the value branch defined (format,
      // pattern, etc.)
      for (const key of Object.keys(valueBranch)) {
        if (key !== 'type') {
          cleaned[key] = valueBranch[key];
        }
      }

      delete cleaned.pattern;
      return cleaned as TopLevelProperty;
    }
  }

  if (
    anyOf &&
    anyOf.length > 0 &&
    anyOf.every((branch) => branch.type === 'string')
  ) {
    delete cleaned.anyOf;
    cleaned.type = 'string';
    delete cleaned.minLength;
    delete cleaned.pattern;
    return cleaned as TopLevelProperty;
  }

  // Drop JSON-Schema noise z.toJSONSchema leaves on each property: the
  // exact RFC 3339 `pattern` on `z.iso.datetime()` (RxDB's storage
  // validator doesn't need it and dexie won't index strings that
  // expensive to compare) and the implicit i53 min/max it stamps onto
  // `z.number().int()` (we set explicit bounds via numericRanges when
  // they actually matter).
  delete cleaned.pattern;
  if (
    (cleaned.type === 'integer' || cleaned.type === 'number') &&
    cleaned.minimum === Number.MIN_SAFE_INTEGER
  ) {
    delete cleaned.minimum;
  }

  if (
    (cleaned.type === 'integer' || cleaned.type === 'number') &&
    cleaned.maximum === Number.MAX_SAFE_INTEGER
  ) {
    delete cleaned.maximum;
  }

  return cleaned as TopLevelProperty;
};

export const rxdbFromZod = <Schema extends z.ZodObject>(
  schema: Schema,
  options: RxdbFromZodOptions<z.infer<Schema>>,
): RxJsonSchema<Omit<z.infer<Schema>, '_deleted'>> => {
  type Doc = Omit<z.infer<Schema>, '_deleted'>;

  // z is the source of truth for shape; we then chip RxDB-specific
  // metadata onto the resulting JSON Schema. Using z.toJSONSchema (vs.
  // hand-walking `_zod.def`) means new Zod field types automatically
  // produce sensible output.
  const json = schema.toJSONSchema() as {
    properties?: Record<string, unknown>;
    required?: string[];
  };

  const properties: Record<string, TopLevelProperty> = {};
  for (const [name, prop] of Object.entries(json.properties ?? {})) {
    if (name === '_deleted') {
      continue;
    }

    properties[name] = stripProperty(prop);
  }

  for (const [name, maxLength] of Object.entries(
    options.stringMaxLength ?? {},
  )) {
    const prop = properties[name] as Record<string, unknown> | undefined;
    if (!prop || typeof maxLength !== 'number') {
      continue;
    }

    const types = Array.isArray(prop.type)
      ? (prop.type as readonly unknown[])
      : [prop.type];
    if (types.includes('string')) {
      prop.maxLength = maxLength;
    }
  }

  // Date-time strings need a deterministic maxLength so RxDB can encode
  // them in its keyed indexes. 30 covers the ISO 8601 forms zod accepts
  // (`YYYY-MM-DDTHH:MM:SS.sssZ` is 24 chars; tz offset variants push it
  // a few more) with headroom. Using dynamic property access because
  // format/maxLength combine with nullable union types that don't fit
  // the narrower TopLevelProperty or StringProp shapes.
  for (const prop of Object.values(properties)) {
    const types = Array.isArray(prop.type)
      ? (prop.type as readonly unknown[])
      : [prop.type];
    if (
      types.includes('string') &&
      (prop as Record<string, unknown>).format === 'date-time' &&
      (prop as Record<string, unknown>).maxLength === undefined
    ) {
      (prop as Record<string, unknown>).maxLength = DATE_TIME_MAX_LENGTH;
    }
  }

  // primaryKey must also have maxLength — RxDB enforces this at runtime,
  // not just at type-level. Fall back to 64 if the caller forgot.
  const pkProp = properties[options.primaryKey] as
    | Record<string, unknown>
    | undefined;
  if (pkProp) {
    const pkTypes = Array.isArray(pkProp.type)
      ? (pkProp.type as readonly unknown[])
      : [pkProp.type];
    if (pkTypes.includes('string') && pkProp.maxLength === undefined) {
      pkProp.maxLength = 64;
    }
  }

  for (const [name, range] of Object.entries(options.numericRanges ?? {})) {
    const prop = properties[name] as NumericProp | undefined;
    if (!prop || !range) {
      continue;
    }

    if (range.min !== undefined) {
      prop.minimum = range.min;
    }

    if (range.max !== undefined) {
      prop.maximum = range.max;
    }

    if (range.multipleOf !== undefined) {
      prop.multipleOf = range.multipleOf;
    }
  }

  const required = (json.required ?? []).filter((key) => key !== '_deleted');

  return {
    indexes: options.indexes as RxJsonSchema<Doc>['indexes'],
    primaryKey: options.primaryKey as RxJsonSchema<Doc>['primaryKey'],
    properties: properties as RxJsonSchema<Doc>['properties'],
    required: required as RxJsonSchema<Doc>['required'],
    type: 'object',
    version: options.version,
  };
};
